import React, { useState, useEffect } from 'react';
import { Ruler, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { authFetch, API } from '../../api';

export default function MeasurablesScore({ playerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch(`${API}/api/measurables/score/${playerId}`);
        if (res.ok) setData(await res.json());
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [playerId]);

  if (loading) return null;
  if (!data || !data.has_score) return null;

  const getScoreColor = (pct) => {
    if (pct >= 90) return '#00E676';
    if (pct >= 75) return '#00D4FF';
    if (pct >= 60) return '#FFD600';
    return '#EF5350';
  };

  const getIcon = (met) => {
    if (met === null) return <span className="text-gray-500 text-xs">—</span>;
    if (met) return <CheckCircle className="w-4 h-4 text-green-400" />;
    return <XCircle className="w-4 h-4 text-red-400" />;
  };

  const overallColor = getScoreColor(data.overall_pct);

  return (
    <div className="panel-card mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-bold text-base flex items-center gap-2">
          <Ruler className="w-5 h-5 text-[#00D4FF]" />
          Measurables Score
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-gray-400 text-xs">{data.scored_count}/{data.total_measurables} measured</span>
          <div className="text-right">
            <div className="text-3xl font-bold" style={{ color: overallColor }}>{data.overall_pct}%</div>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {data.details.map((d, idx) => {
          const pct = d.pct;
          const barColor = pct !== null ? getScoreColor(pct) : '#546E7A';

          return (
            /* Four fixed columns do not fit a phone, so on mobile this wraps
               into: [icon + name + pct] / [bar] / [actual vs target]. */
            <div key={idx} className="flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-2">
              <span className="order-1 md:order-none flex-shrink-0">{getIcon(d.met)}</span>
              <div className="order-2 md:order-none flex-1 md:flex-none md:w-32 text-sm text-gray-300 min-w-0 truncate">{d.name}</div>
              <div className="order-3 md:order-none w-12 text-right text-xs font-bold flex-shrink-0" style={{ color: pct !== null ? barColor : '#546E7A' }}>
                {pct !== null ? `${pct}%` : '—'}
              </div>
              <div className="order-4 md:order-none w-full md:w-auto md:flex-1 h-2.5 bg-[#0A0E27] rounded-full overflow-hidden">
                {pct !== null && (
                  <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, background: barColor }} />
                )}
              </div>
              <div className="order-5 md:order-none w-full md:w-28 text-left md:text-right text-xs flex-shrink-0">
                {d.actual !== null ? (
                  <span>
                    <span className="text-white font-bold">{d.actual}{d.unit ? ' ' + d.unit : ''}</span>
                    <span className="text-gray-500 ml-1">/ {d.operator} {d.target}</span>
                  </span>
                ) : (
                  <span className="text-gray-500">Not recorded</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
